import React, { useState, useContext, useEffect, useRef } from 'react';
import { AppContext } from '../../../App';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import { IField } from '../../../types/my-types';
import {
  addCurrentChargesSumCheck,
  addRequiredFieldValidator,
  removeCurrentChargesSumCheck,
  removeRequiredFieldValidator,
  isFieldRequired,
  fieldHasCurrentChargesSumCheck,
  getExistingSumcheckConfig,
  isCustomSumcheckConfig,
  DEFAULT_SUMCHECK_CONFIG,
  SumcheckConfigInput,
} from '../../../utils/fieldValidations';
import { useFieldUpdate } from '../../hooks/useFieldUpdate';
import { useQueryClient } from '@tanstack/react-query';
import errorResponses from '../../../errorResponses';
import ChargesSumcheckConfigDialog from '../../tool/fieldModal/ChargesSumcheckConfigDialog';

interface IValidationSettingsProps {
  field: IField;
  trainingSetId?: string;
}

interface IValidationSettingsFormValues {
  fieldRequired: boolean;
  currentChargesSumCheck: boolean;
  sumcheckConfig: SumcheckConfigInput | null;
}

const ValidationSettings: React.FC<IValidationSettingsProps> = ({ field, trainingSetId }) => {
  const { setToast } = useContext(AppContext);

  const queryClient = useQueryClient();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const isCurrentlyRequired = isFieldRequired(field.validation_config ?? null);
  const isCurrentChargesSumCheck = fieldHasCurrentChargesSumCheck(field.validation_config ?? null);
  const existingSumcheckConfig = getExistingSumcheckConfig(field.validation_config ?? null);

  const [open, setOpen] = useState(false);
  const [sumcheckConfigDialogOpen, setSumcheckConfigDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState<IValidationSettingsFormValues>({
    fieldRequired: isCurrentlyRequired,
    currentChargesSumCheck: isCurrentChargesSumCheck,
    sumcheckConfig: existingSumcheckConfig,
  });

  // Track previous open state to reset form values when dialog opens
  const prevOpenRef = useRef(false);

  // Reset form values when dialog opens to ensure fresh data from field prop
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setFormValues({
        fieldRequired: isCurrentlyRequired,
        currentChargesSumCheck: isCurrentChargesSumCheck,
        sumcheckConfig: existingSumcheckConfig,
      });
    }
    prevOpenRef.current = open;
  }, [open, isCurrentlyRequired, isCurrentChargesSumCheck, existingSumcheckConfig]);

  // Determine if the current config differs from the default
  const hasCustomSumcheckConfig = isCustomSumcheckConfig(formValues.sumcheckConfig);

  const fieldUpdateMutation = useFieldUpdate({
    onSuccess: () => {
      setToast({
        open: true,
        message: 'Field Updated.',
        variant: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: ['fields']
      });
      setOpen(false);
    },
    onError: () => {
      setToast({
        open: true,
        message: errorResponses.failedToRenameField,
        variant: 'error',
      });
    },
  }, trainingSetId);

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    let validationConfigRequestData = formValues.fieldRequired
      ? addRequiredFieldValidator(field.validation_config ?? null)
      : removeRequiredFieldValidator(field.validation_config ?? null);
    validationConfigRequestData = formValues.currentChargesSumCheck
      ? addCurrentChargesSumCheck(validationConfigRequestData ?? null, formValues.sumcheckConfig)
      : removeCurrentChargesSumCheck(validationConfigRequestData ?? null);

    fieldUpdateMutation.mutate({
      id: field.id,
      data: {
        validation_config: validationConfigRequestData,
      },
    });
  };

  return (
    <>
      <Tooltip title="Field Validations" placement="top">
        <IconButton
          onClick={handleOpen}
          size="small"
          data-cy={`btn-field-validations-${field.label}`}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose}>
        <form onSubmit={handleFormSubmit}>
          <DialogTitle>Field Validations</DialogTitle>
          <DialogContent>
            <FormControlLabel
              control={
                <Checkbox
                  data-cy="checkbox-validations-required"
                  checked={formValues.fieldRequired}
                  onChange={(e) =>
                    setFormValues((prevValues) => ({
                      ...prevValues,
                      fieldRequired: e.target.checked,
                    }))
                  }
                />
              }
              label="Required"
            />
            <br />
            <FormControlLabel
              control={
                <Checkbox
                  data-cy="checkbox-validations-sumcheck"
                  onChange={(e) =>
                    setFormValues((prevValues) => ({
                      ...prevValues,
                      currentChargesSumCheck: e.target.checked,
                    }))
                  }
                  checked={formValues.currentChargesSumCheck}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>Charges Sumcheck</span>
                  {formValues.currentChargesSumCheck && (
                    <Tooltip title="Configure include/exclude patterns" placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          setSumcheckConfigDialogOpen(true);
                        }}
                        data-cy="btn-configure-sumcheck-validations"
                        sx={{ p: 0.25 }}
                      >
                        <EditIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {formValues.currentChargesSumCheck && hasCustomSumcheckConfig && (
                    <Typography
                      component="span"
                      variant="caption"
                      onClick={(e) => e.preventDefault()}
                      sx={{
                        ml: 0.5,
                        px: 0.75,
                        py: 0.25,
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                        borderRadius: 1,
                        fontSize: '0.65rem',
                        fontWeight: 500,
                      }}
                    >
                      Custom
                    </Typography>
                  )}
                </Box>
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
            <Button
              data-cy="btn-field-validations-save"
              type="submit"
              variant="contained"
              color="primary"
              disabled={fieldUpdateMutation.isPending}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Sumcheck Configuration Dialog */}
      <ChargesSumcheckConfigDialog
        open={sumcheckConfigDialogOpen}
        config={
          formValues.sumcheckConfig || {
            include: [...DEFAULT_SUMCHECK_CONFIG.include],
            exclude: [...DEFAULT_SUMCHECK_CONFIG.exclude],
          }
        }
        onClose={() => setSumcheckConfigDialogOpen(false)}
        onSave={(newConfig) => {
          setFormValues((prevValues) => ({
            ...prevValues,
            sumcheckConfig: newConfig,
          }));
        }}
      />
    </>
  );
};

export default ValidationSettings;
